import { createContext, useContext, useEffect, useState, useRef } from "react";
import keycloak from "./keycloack";
import getEnv from "./config";

const KeycloakContext = createContext({ keycloak: null, initialized: false, authError: null });

export const useKeycloak = () => useContext(KeycloakContext);

const REDIRECT_URI = window.location.origin + "/";

// Read directly from env vars — keycloak-js v26 may not set properties synchronously
const KC_HOST = getEnv("REACT_APP_KEYCLOAK_HOST").replace(/\/+$/, "");
const KC_REALM = getEnv("REACT_APP_KEYCLOAK_REALM", "M4D-CTI");
const KC_CLIENT_ID = getEnv("REACT_APP_KEYCLOAK_CLIENT_PUBLIC", "cti-public-client");

const TOKEN_STORAGE_KEY = "kc_tokens";

// Generate random string for state parameter
function randomState() {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}

// Extract query param from URL
function getQueryParam(name) {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

function saveTokens(data) {
  sessionStorage.setItem(
    TOKEN_STORAGE_KEY,
    JSON.stringify({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      id_token: data.id_token,
    })
  );
}

function loadTokens() {
  try {
    const raw = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearTokens() {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
}

function initKeycloakWithTokens(tokens) {
  return keycloak.init({
    token: tokens.access_token,
    refreshToken: tokens.refresh_token,
    idToken: tokens.id_token,
    checkLoginIframe: false,
    timeSkew: 0,
  });
}

function redirectToLogin() {
  if (!KC_HOST) {
    console.error("[KC] REACT_APP_KEYCLOAK_HOST is empty — cannot redirect to login");
    return false;
  }

  const state = randomState();
  sessionStorage.setItem("kc_oidc_state", state);

  const authUrl =
    KC_HOST +
    "/realms/" +
    KC_REALM +
    "/protocol/openid-connect/auth?" +
    new URLSearchParams({
      client_id: KC_CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      response_type: "code",
      response_mode: "query",
      scope: "openid",
      state: state,
    }).toString();

  console.log("[KC] Redirecting to login:", authUrl);
  window.location.replace(authUrl);
  return true;
}

export const KeycloakProvider = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [authError, setAuthError] = useState(null);
  const didInit = useRef(false);

  function loginOrFail() {
    if (!redirectToLogin()) {
      setAuthError("Keycloak host is not configured (REACT_APP_KEYCLOAK_HOST is empty)");
      setInitialized(true);
    }
  }

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    const code = getQueryParam("code");
    const stateFromUrl = getQueryParam("state");
    const savedState = sessionStorage.getItem("kc_oidc_state");

    console.log("[KC] Provider start", { code: !!code, stateFromUrl, savedState, url: window.location.href });

    if (code) {
      // We have a code from Keycloak — exchange it for tokens
      console.log("[KC] Exchanging code for tokens...");

      // Validate state
      if (stateFromUrl && savedState && stateFromUrl !== savedState) {
        setAuthError("State mismatch: expected " + savedState + " got " + stateFromUrl);
        setInitialized(true);
        return;
      }

      sessionStorage.removeItem("kc_oidc_state");

      // Clean URL immediately
      window.history.replaceState({}, "", REDIRECT_URI);

      const tokenUrl = KC_HOST + "/realms/" + KC_REALM + "/protocol/openid-connect/token";
      console.log("[KC] Token URL:", tokenUrl);

      fetch(tokenUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          grant_type: "authorization_code",
          client_id: KC_CLIENT_ID,
          code: code,
          redirect_uri: REDIRECT_URI,
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.access_token) {
            console.log("[KC] Token exchange success!");
            saveTokens(data);
            return initKeycloakWithTokens(data).then(() => {
              console.log("[KC] Keycloak initialized with tokens, authenticated:", keycloak.authenticated);
              if (!keycloak.authenticated) {
                console.warn("[KC] keycloak.authenticated is false after init — forcing authenticated state");
              }
              setInitialized(true);
            });
          } else {
            console.error("[KC] Token exchange failed:", data);
            clearTokens();
            setAuthError(
              "Token exchange failed: " + (data.error_description || data.error || JSON.stringify(data))
            );
            setInitialized(true);
          }
        })
        .catch((err) => {
          console.error("[KC] Token exchange network error:", err);
          clearTokens();
          setAuthError("Token exchange network error: " + err.message);
          setInitialized(true);
        });
    } else {
      // No code — check if we have stored tokens from a previous login
      const storedTokens = loadTokens();

      if (storedTokens) {
        console.log("[KC] Found stored tokens, attempting to restore session...");
        initKeycloakWithTokens(storedTokens)
          .then(() => {
            if (keycloak.authenticated) {
              console.log("[KC] Session restored from stored tokens");
              setInitialized(true);
            } else {
              // Tokens expired or invalid — try refreshing
              console.log("[KC] Stored tokens invalid, attempting refresh...");
              return keycloak
                .updateToken(-1) // force refresh
                .then(() => {
                  console.log("[KC] Token refresh succeeded");
                  saveTokens({
                    access_token: keycloak.token,
                    refresh_token: keycloak.refreshToken,
                    id_token: keycloak.idToken,
                  });
                  setInitialized(true);
                })
                .catch(() => {
                  console.log("[KC] Token refresh failed, redirecting to login");
                  clearTokens();
                  loginOrFail();
                });
            }
          })
          .catch((err) => {
            console.warn("[KC] Failed to restore session:", err);
            clearTokens();
            loginOrFail();
          });
      } else {
        // No stored tokens, no code — redirect to login
        loginOrFail();
      }
    }
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (!initialized || !keycloak.authenticated) return;

    const interval = setInterval(() => {
      keycloak
        .updateToken(60) // refresh if expiring within 60 seconds
        .then((refreshed) => {
          if (refreshed) {
            console.log("[KC] Token refreshed");
            saveTokens({
              access_token: keycloak.token,
              refresh_token: keycloak.refreshToken,
              id_token: keycloak.idToken,
            });
          }
        })
        .catch(() => {
          console.warn("[KC] Token refresh failed, clearing session");
          clearTokens();
          loginOrFail();
        });
    }, 30000); // check every 30 seconds

    return () => clearInterval(interval);
  }, [initialized]);

  return (
    <KeycloakContext.Provider value={{ keycloak, initialized, authError }}>
      {children}
    </KeycloakContext.Provider>
  );
};
